/* 잰 값 읽기와 정합성 대조, 유사도 */
import { ComputeEngine } from '@cortex-js/compute-engine';
import type { MathcoreApp } from './tools';

const ce = new ComputeEngine();

/** expression 패키지 nodes.ts 의 CategoryType.IMMEDIATE_EXPRESSION 의 순번(EMPTY 부터 0) */
const IMMEDIATE_EXPRESSION = 8;

export function readMeasurements(app: MathcoreApp): { id: string; name: string; kind: string; value: number }[] {
  const manager: any = (app as any).objectManager;
  const objects: any[] = manager?.getAllObjects?.() ?? [];
  const out: { id: string; name: string; kind: string; value: number }[] = [];
  for (const o of objects) {
    // 정적분 식은 대수 개체가 값을 계산한다(값만 갖는 식이면서 영역 칠하기가 선 것은 정적분뿐이다: algebra-system.ts 의 DirectDefiniteIntegralNode 갈래)
    if (o?.type === 'polynomial' && o.expressionCategory === IMMEDIATE_EXPRESSION && o.inequalityExists === true && o.isValid !== false && Number.isFinite(o.immediateValue)) { out.push({ id: String(o.id), name: String(o.name ?? ''), kind: '정적분', value: o.immediateValue }); continue; }
    // 측정 객체의 type 은 measureLength, measureAngle, measureArea, measureGradient (object-manager objects/type.ts)
    if (typeof o?.type !== 'string' || !o.type.startsWith('measure') || o.isValid === false) continue;
    const kind = o.type === 'measureArea' ? '넓이' : o.type === 'measureLength' ? '길이' : o.type === 'measureAngle' ? '각' : o.type === 'measureGradient' ? '기울기' : o.type;
    const value = typeof o.area === 'number' ? o.area : typeof o.length === 'number' ? o.length : typeof o.angle === 'number' ? o.angle : typeof o.gradient === 'number' ? o.gradient : Number.NaN;
    if (Number.isFinite(value)) out.push({ id: String(o.id), name: String(o.name ?? ''), kind, value });
  }
  return out;
}

export function tokens(text: string): string[] {
  const out: string[] = [];
  const cleaned = text.replace(/\\[a-zA-Z]+/g, (m) => ` ${m.slice(1)} `);
  for (const word of cleaned.split(/[^가-힣a-zA-Z0-9]+/).filter(Boolean)) {
    if (/^[가-힣]+$/.test(word)) {
      if (word.length === 1) out.push(word);
      for (let i = 0; i + 1 < word.length; i += 1) out.push(word.slice(i, i + 2));
    } else {
      out.push(word.toLowerCase());
    }
  }
  return out;
}

/** 한글 bigram 집합의 자카드 유사도(%) */
export function similarityPercent(a: string, b: string): number {
  const A = new Set(tokens(a)); const B = new Set(tokens(b));
  if (!A.size || !B.size) return 0;
  let both = 0;
  for (const t of A) if (B.has(t)) both += 1;
  return Math.round((100 * both) / (A.size + B.size - both));
}

/** 수 또는 식 문자열을 수로. 문자열은 Compute Engine 이 읽는 그대로이고 고쳐 쓰지 않는다 */
export function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim()) {
    try {
      const value = Number(ce.parse(v.trim()).N().valueOf());
      return Number.isFinite(value) ? value : null;
    } catch { return null; }
  }
  return null;
}

/** 답의 수를 도구가 잰 값과 대조한다. 측정이 있을 때만 판정한다 */
/** 잰 값과 대조한다. target 은 그림에서 잴 값(답이 p+q 처럼 잰 값에서 유도되면 그 바탕 값). 없으면 답 자체 */
export function verifyAgainstMeasured(answer: number | null, values: number[], target: number | null = null): { verdict: 'ok' | 'mismatch' | 'none'; detail: string } {
  const measured = values.filter((v) => Number.isFinite(v));
  if (!measured.length) return { verdict: 'none', detail: '도구가 잰 값이 없다' };
  const goal = target !== null && Number.isFinite(target) ? target : answer;
  if (goal === null || !Number.isFinite(goal)) return { verdict: 'none', detail: '답이 수가 아니다' };
  const label = goal === answer ? `답 ${answer}` : `잴 값 ${goal}(답 ${answer} 의 바탕)`;
  const hit = measured.find((m) => Math.abs(m - goal) <= Math.max(0.05, Math.abs(goal) * 0.02));
  return hit !== undefined
    ? { verdict: 'ok', detail: `${label} ≈ 잰 값 ${hit.toFixed(3)}` }
    : { verdict: 'mismatch', detail: `${label} 와 잰 값 [${measured.map((m) => m.toFixed(3)).join(', ')}] 이 다르다` };
}

/** 두 값이 허용오차(0.05 와 2% 중 큰 값) 안에서 같은가 */
export function closeEnough(a: number, goal: number): boolean {
  return Number.isFinite(a) && Number.isFinite(goal) && Math.abs(a - goal) <= Math.max(0.05, Math.abs(goal) * 0.02);
}
